import type { Message, Part } from "@mux/sdk/v2/client"
import { mergeOptimisticPage, sortOptimisticItem, type MessagePage, type OptimisticItem } from "./optimistic"

type Update = {
  directory: string
  sessionID: string
  message: Message
  parts: Part[]
}

export function createSessionOptimisticController(input: {
  addLocal(update: Omit<Update, "directory">): void
  removeLocal(update: { sessionID: string; messageID: string }): void
}) {
  const optimistic = new Map<string, Map<string, OptimisticItem>>()
  const key = (directory: string, sessionID: string) => `${directory}\n${sessionID}`

  const set = (store: Map<string, Map<string, OptimisticItem>>, update: Update) => {
    const id = key(update.directory, update.sessionID)
    const items = store.get(id) ?? new Map<string, OptimisticItem>()
    items.set(update.message.id, sortOptimisticItem(update))
    store.set(id, items)
    input.addLocal(update)
  }
  const remove = (
    store: Map<string, Map<string, OptimisticItem>>,
    update: { directory: string; sessionID: string; messageID: string },
    local = true,
  ) => {
    const id = key(update.directory, update.sessionID)
    const items = store.get(id)
    const owned = items?.delete(update.messageID) ?? false
    if (items?.size === 0) store.delete(id)
    // Once a page has confirmed the message, the server's copy owns it; a late rollback must not erase it.
    if (local && owned) input.removeLocal(update)
  }
  const values = (store: Map<string, Map<string, OptimisticItem>>, directory: string, sessionID: string) =>
    store.get(key(directory, sessionID))?.values() ?? []

  return {
    optimistic: {
      add(update: Update) {
        set(optimistic, update)
      },
      remove(update: { directory: string; sessionID: string; messageID: string }) {
        remove(optimistic, update)
      },
    },
    merge(directory: string, sessionID: string, page: MessagePage) {
      const pending = new Map<string, OptimisticItem>()
      for (const item of values(optimistic, directory, sessionID)) pending.set(item.message.id, item)
      const merged = mergeOptimisticPage(page, [...pending.values()])
      for (const messageID of merged.confirmed) {
        remove(optimistic, { directory, sessionID, messageID }, false)
      }
      return merged
    },
    forgetMessages(directory: string, sessionID: string, messageIDs: Iterable<string>) {
      for (const messageID of messageIDs) remove(optimistic, { directory, sessionID, messageID }, false)
    },
    has(directory: string, sessionID: string, messageID: string) {
      return optimistic.get(key(directory, sessionID))?.has(messageID) ?? false
    },
    /** IDs of this session's messages the server has not confirmed yet. */
    pending(directory: string, sessionID: string) {
      return [...(optimistic.get(key(directory, sessionID))?.keys() ?? [])]
    },
    clear(directory: string, sessionIDs: Iterable<string>) {
      for (const sessionID of sessionIDs) {
        optimistic.delete(key(directory, sessionID))
      }
    },
    dispose() {
      optimistic.clear()
    },
  }
}
